
import { namespace } from "vuex-class";
import { Vue, Component, Prop, Watch, PropSync } from "vue-property-decorator";
import { Customization } from "@/store/modules/customization";
import { TtsVoiceVm } from "@/interfaces/TtsModelVm";
import { CustomizationVm } from "@/interfaces/CustomizationVm";

type VoiceType = "standard" | "podcast";

const globals = namespace("globals");
const customization = namespace("customization");

@Component({})
export default class TtsVoiceSelection extends Vue {
  @Prop({ type: String, default: "standard" })
  voiceType!: VoiceType;

  @Prop({ type: Boolean, default: true })
  showLanguageSelector!: boolean;

  @PropSync("selectedLanguage", { type: String, default: "de-DE" })
  syncedSelectedLanguage!: string;

  @PropSync("selectedVoiceId", { type: String, default: "" })
  syncedSelectedVoiceId!: string;

  @globals.Getter selectedLanguageCode!: string | null;
  @customization.Getter customizationSettings!: CustomizationVm;
  @customization.Getter ttsStandardVoices!: { [key: string]: TtsVoiceVm[]; } | null;
  @customization.Getter ttsPodcastVoices!: { [key: string]: TtsVoiceVm[]; } | null;

  get voices(): { [key: string]: TtsVoiceVm[] } | null {
    return this.voiceType === "standard"
      ? this.ttsStandardVoices
      : this.ttsPodcastVoices;
  }

  get availableLanguagesWithNames(): { code: string; name: string }[] {
    if (!this.voices) return [];
    return Object.keys(this.voices).map(code => ({
      code,
      name: this.$globalHelper.getLanguageNameFromCode(code),
    }));
  }

  get availableVoices(): TtsVoiceVm[] {
    if (!this.voices || !this.syncedSelectedLanguage) return [];
    return this.voices[this.syncedSelectedLanguage] || [];
  }

  mounted() {
    this.initializeSelection();
  }

  @Watch("voiceType")
  onVoiceTypeChange() {
    this.initializeSelection();
  }

  @Watch("syncedSelectedLanguage")
  onSyncedSelectedLanguageChange(newVal: string, oldVal: string) {
    // console.log("TtsVoiceSelection - syncedSelectedLanguage changed from", oldVal, "to", newVal);
    this.onLanguageChange();
  }

  private async initializeSelection() {
    // console.log("syncedSelectedLanguage:", this.syncedSelectedLanguage);
    // console.log("syncedSelectedVoiceId:", this.syncedSelectedVoiceId);
    // console.log("availableLanguagesWithNames:", this.availableLanguagesWithNames);
    // console.log("selectedLanguageCode:", this.selectedLanguageCode);

    if (!this.syncedSelectedLanguage && this.availableLanguagesWithNames.length > 0) {
      // look for this.selectedLanguageCode in available languages
      if (this.selectedLanguageCode) {
        // console.log("TtsVoiceSelection - Checking if selectedLanguageCode is available:", this.selectedLanguageCode);
        const languageExists = this.availableLanguagesWithNames.some(lang => lang.code === this.selectedLanguageCode);
        if (languageExists) {
          // console.log("TtsVoiceSelection - Initializing language selection from selectedLanguageCode:", this.selectedLanguageCode);
          this.syncedSelectedLanguage = this.selectedLanguageCode;
          await this.$globalHelper.delay(5);
        } else {
          // Try to find a language that starts with the same prefix (e.g. "en" for "en-US")
          const prefix = this.selectedLanguageCode.split("-")[0];
          const languageWithPrefix = this.availableLanguagesWithNames.find(lang => lang.code.startsWith(prefix));
          if (languageWithPrefix) {
            this.syncedSelectedLanguage = languageWithPrefix.code;
            await this.$globalHelper.delay(5);
          }
        }
      }

      // Select first available language if none selected
      if (!this.syncedSelectedLanguage) {
        // console.log("TtsVoiceSelection - Initializing language selection:", this.availableLanguagesWithNames[0].code);
        this.syncedSelectedLanguage = this.availableLanguagesWithNames[0].code;
        await this.$globalHelper.delay(5);
      }
    }

    // Check if selected voice is available for that language
    // console.log("TtsVoiceSelection - Initializing selection for voiceType:", this.voiceType);
    const voiceAvailable = this.availableVoices.some(v => v.voiceId === this.syncedSelectedVoiceId);
    if (!voiceAvailable) {
      // console.log("TtsVoiceSelection - Selected voice not available for language:", this.syncedSelectedLanguage, this.syncedSelectedVoiceId);
      if ( this.availableVoices.length > 0) {
        // console.log("TtsVoiceSelection - Initializing voice selection:", this.availableVoices[0].voiceId);
        this.syncedSelectedVoiceId = this.availableVoices[0].voiceId || "";
      } else {
        // console.log("TtsVoiceSelection - No available voices for language:", this.syncedSelectedLanguage);
        this.syncedSelectedVoiceId = "";
      }
    }

    // Select first voice of that language if none selected
    // console.log("TtsVoiceSelection - Current voice selection:", this.syncedSelectedVoiceId);
    // console.log("TtsVoiceSelection - Available voices for language", this.syncedSelectedLanguage, ":", this.availableVoices);
    // if (!this.syncedSelectedVoiceId && this.availableVoices.length > 0) {
    //   console.log("TtsVoiceSelection - Initializing voice selection:", this.availableVoices[0].voiceId);
    //   this.syncedSelectedVoiceId = this.availableVoices[0].voiceId || "";
    // }
  }

  async onLanguageChange() {
    // Select first voice of newly selected language
    await this.$globalHelper.delay(5);
    // console.log("TtsVoiceSelection - Language changed to:", this.syncedSelectedLanguage);
    if (this.availableVoices.length > 0) {
      // console.log("TtsVoiceSelection - Selecting first voice for new language:", this.availableVoices[0].voiceId || "");
      this.syncedSelectedVoiceId = this.availableVoices[0].voiceId || "";
    } else {
      // console.log("TtsVoiceSelection - No available voices for new language:", this.syncedSelectedLanguage);
      this.syncedSelectedVoiceId = "";
    }
  }
}
